import React from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Chip,
  Divider
} from '@heroui/react';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink } from '@react-pdf/renderer';
import { DocumentTextIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

interface InvoiceItem {
  id: string;
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface InvoiceDetails {
  id: string;
  date: string;
  amount: string;
  status: string;
  plan: string;
  items: InvoiceItem[];
}

// PDF Document Component
const InvoicePDFDocument = ({ invoice }: { invoice: InvoiceDetails }) => {
  const styles = StyleSheet.create({
    page: {
      padding: 30,
      fontFamily: 'Helvetica',
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 20,
    },
    title: {
      fontSize: 24,
      fontWeight: 'bold',
      marginBottom: 20,
      color: '#2563eb',
    },
    companyInfo: {
      fontSize: 10,
      marginBottom: 10,
    },
    invoiceInfo: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 20,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      marginTop: 15,
      marginBottom: 10,
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
      paddingBottom: 3,
    },
    itemRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: '#e5e7eb',
      paddingVertical: 5,
      fontSize: 10,
    },
    itemCell: {
      flex: 1,
      padding: 5,
    },
    totalRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      marginTop: 10,
      paddingTop: 10,
      borderTopWidth: 1,
      borderTopColor: '#e5e7eb',
    },
    totalLabel: {
      fontSize: 12,
      fontWeight: 'bold',
      width: 80,
    },
    totalValue: {
      fontSize: 12,
      fontWeight: 'bold',
    },
    statusChip: {
      paddingVertical: 5,
      paddingHorizontal: 10,
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 'bold',
      textTransform: 'uppercase',
    },
    statusPaid: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    statusPending: {
      backgroundColor: '#fef9c3',
      color: '#854d0e',
    },
    statusFailed: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
  });

  const getStatusStyle = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return [styles.statusChip, styles.statusPaid];
      case 'pending':
        return [styles.statusChip, styles.statusPending];
      default:
        return [styles.statusChip, styles.statusFailed];
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View>
            <Text style={styles.title}>Recibo de Pago</Text>
            <Text style={styles.companyInfo}>Macro Industrial</Text>
            <Text style={styles.companyInfo}>Calle 123 #45-67</Text>
            <Text style={styles.companyInfo}>Medellín, Colombia</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text>Factura: {invoice.id}</Text>
            <Text>Fecha: {new Date(invoice.date).toLocaleDateString()}</Text>
            <View style={getStatusStyle(invoice.status)}>
              <Text>{invoice.status === 'paid' ? 'Pagado' : invoice.status === 'pending' ? 'Pendiente' : 'Fallido'}</Text>
            </View>
          </View>
        </View>

        <View style={styles.invoiceInfo}>
          <View>
            <Text style={{ fontSize: 14, fontWeight: 'bold' }}>Detalles del Plan:</Text>
            <Text>Plan: {invoice.plan}</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={{ fontSize: 14, fontWeight: 'bold' }}>Total:</Text>
            <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#2563eb' }}>${(invoice.items.reduce((sum, item) => sum + item.total, 0)).toLocaleString('es-CO')} COP</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Detalles del Cargo</Text>
        <View style={styles.itemRow}>
          <Text style={[styles.itemCell, { fontWeight: 'bold' }]}>Descripción</Text>
          <Text style={[styles.itemCell, { fontWeight: 'bold', textAlign: 'center' }]}>Cantidad</Text>
          <Text style={[styles.itemCell, { fontWeight: 'bold', textAlign: 'right' }]}>Precio Unitario</Text>
          <Text style={[styles.itemCell, { fontWeight: 'bold', textAlign: 'right' }]}>Total</Text>
        </View>

        {invoice.items.map((item, index) => (
          <View key={index} style={styles.itemRow}>
            <Text style={styles.itemCell}>{item.name}</Text>
            <Text style={[styles.itemCell, { textAlign: 'center' }]}>{item.quantity}</Text>
            <Text style={[styles.itemCell, { textAlign: 'right' }]}>${item.unitPrice.toLocaleString('es-CO')}</Text>
            <Text style={[styles.itemCell, { textAlign: 'right' }]}>${item.total.toLocaleString('es-CO')}</Text>
          </View>
        ))}

        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>TOTAL:</Text>
          <Text style={styles.totalValue}>${(invoice.items.reduce((sum, item) => sum + item.total, 0)).toLocaleString('es-CO')} COP</Text>
        </View>
      </Page>
    </Document>
  );
};

interface InvoiceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: InvoiceDetails | null;
}

const InvoiceDetailsModal: React.FC<InvoiceDetailsModalProps> = ({ 
  isOpen, 
  onClose, 
  invoice 
}) => {
  if (!invoice) return null;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      size="3xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <DocumentTextIcon className="w-6 h-6 text-primary" />
                <h3 className="text-xl font-semibold">Detalles de la Factura</h3>
              </div>
              <div className="text-sm text-gray-500">
                Factura: {invoice.id} | Fecha: {new Date(invoice.date).toLocaleDateString()}
              </div>
            </ModalHeader>
            <ModalBody>
              <div className="border rounded-lg p-4 mb-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-lg font-semibold">Resumen de la Factura</h4>
                    <p className="text-default-500">Información general de la factura</p>
                  </div>
                  <Chip 
                    color={invoice.status === 'paid' ? 'success' : invoice.status === 'pending' ? 'warning' : 'danger'}
                    variant="flat"
                    size="md"
                  >
                    {invoice.status === 'paid' ? 'Pagado' : invoice.status === 'pending' ? 'Pendiente' : 'Fallido'}
                  </Chip>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">ID de Factura</p>
                    <p className="font-medium">{invoice.id}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Fecha</p>
                    <p className="font-medium">{new Date(invoice.date).toLocaleDateString()}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Plan</p>
                    <p className="font-medium">{invoice.plan}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-500">Monto Total</p>
                    <p className="font-medium text-xl text-primary font-bold">{invoice.amount}</p>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="text-lg font-semibold mb-4">Detalles del Cargo</h4>
                <div className="space-y-4">
                  {invoice.items.map((item, index) => (
                    <div key={index} className="border-b pb-3 last:border-0 last:pb-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <h5 className="font-medium">{item.name}</h5>
                          <p className="text-sm text-gray-500">
                            {item.quantity} unidades × ${item.unitPrice.toLocaleString('es-CO')} COP
                          </p>
                        </div>
                        <p className="font-semibold">
                          ${item.total.toLocaleString('es-CO')} COP
                        </p>
                      </div>
                      
                      {/* Detailed explanation of the charge */}
                      {item.name.includes('usuarios') && (
                        <div className="mt-2 text-sm text-gray-600">
                          <p>Detalle del cargo:</p>
                          <ul className="list-disc pl-5 space-y-1 mt-1">
                            <li>{item.quantity} usuarios registrados</li>
                            <li>Valor adicional por usuario: {'$' + (10000).toLocaleString('es-CO') + ' COP'}</li>
                            <li>Valor total adicional: ${item.total.toLocaleString('es-CO')} COP</li>
                          </ul>
                        </div>
                      )}
                      
                      {item.name.includes('documentos') && (
                        <div className="mt-2 text-sm text-gray-600">
                          <p>Detalle del cargo:</p>
                          <ul className="list-disc pl-5 space-y-1 mt-1">
                            <li>{item.quantity} documentos</li>
                            <li>Valor adicional por documento: {'$' + (500).toLocaleString('es-CO') + ' COP'}</li>
                            <li>Valor total adicional: ${item.total.toLocaleString('es-CO')} COP</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <Divider className="my-4" />
                
                <div className="flex justify-end">
                  <div className="w-48">
                    <div className="flex justify-between py-2">
                      <span className="text-gray-500">Subtotal:</span>
                      <span>
                        ${(invoice.items.reduce((sum, item) => sum + item.total, 0)).toLocaleString('es-CO')} COP
                      </span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-500">IVA (0%):</span>
                      <span>$0 COP</span>
                    </div>
                    <Divider />
                    <div className="flex justify-between py-2 font-bold text-lg">
                      <span>Total:</span>
                      <span className="text-primary">{invoice.amount}</span>
                    </div>
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="default" variant="light" onClick={onClose}>
                Cerrar
              </Button>
              <PDFDownloadLink 
                document={<InvoicePDFDocument invoice={invoice} />} 
                fileName={`factura-${invoice.id}.pdf`}
              >
                {({ loading }) => (
                  <Button 
                    color="primary" 
                    startContent={<ArrowDownTrayIcon className="w-4 h-4" />}
                  >
                    {loading ? 'Generando...' : 'Descargar Recibo'}
                  </Button>
                )}
              </PDFDownloadLink>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default InvoiceDetailsModal;